import axiosInstance from "../../utility/axios";
import {
  createOpportunity,
  createOpportunityType,
  deleteOpportunity,
  deleteType,
  disableOpportunityLoading,
  editOpportunity,
  setOpportunity,
  setOpportunityLoading,
  setOpportunityLoadingId,
  setOpportunityTypeLoadingId,
  setOpportunityTypes,
  setOpportunities,
  setUpload,
  setUpdatingOpportunityId,
  updateOpportunityStatus,
  setTotalOpportunity,
} from "../../store/slices/opportunity";
import { AppDispatch } from "../../store/store";
import {
  ICreateOpportunity,
  ICreateOpportunityResponse,
  ICreateOpportunityTypeResponse,
  IEditOpportunity,
  IOpportunity,
  IOpportunitiesResponse,
  IOpportunityTypesResponse,
  OpportunityTypeCreate,
  IOpportunityCount,
} from "../../store/types/opportunity";
import { toast } from "sonner";
import { getErrorMessage } from "../apiError";

const opportunityEndpoint = "/product/api/v1/admin";
const opportunitiesUrl = `${opportunityEndpoint}/opportunities`;
const opportunityTypesUrl = `${opportunityEndpoint}/opportunity-types`;

const assertSuccess = (success: boolean, message: string, fallback: string) => {
  if (!success) throw new Error(message || fallback);
};

export const get_opportunities = async (
  dispatch: AppDispatch,
  limit = 20,
  offset = 0,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const response = await axiosInstance.get<IOpportunitiesResponse>(
      opportunitiesUrl,
      { params: { limit, offset } },
    );
    const { success, message, data } = response.data;
    assertSuccess(success, message, "Failed to load opportunities");

    if (Array.isArray(data)) {
      dispatch(
        setOpportunities({
          data,
          limit,
          offset,
          total: data.length,
        }),
      );
    } else if (data && Array.isArray(data.data)) {
      dispatch(
        setOpportunities({
          data: data.data,
          limit: data.limit ?? limit,
          offset: data.offset ?? offset,
          total: data.total ?? data.data.length,
        }),
      );
    } else {
      throw new Error("Opportunity list response has an invalid data format");
    }
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load opportunities"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};

export const get_opportunity = async (
  dispatch: AppDispatch,
  id: string,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const response = await axiosInstance.get<{
      success: boolean;
      message: string;
      data: IOpportunity;
    }>(`${opportunitiesUrl}/${encodeURIComponent(id)}`);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to load opportunity",
    );
    dispatch(setOpportunity(response.data.data));
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load opportunity"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};

export const create_opportunity = async (
  formData: ICreateOpportunity,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const response = await axiosInstance.post<ICreateOpportunityResponse>(
      opportunitiesUrl,
      formData,
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to create opportunity",
    );
    dispatch(createOpportunity(response.data.data));
    toast.success(response.data.message || "Opportunity created successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to create opportunity"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};

export const edit_opportunity = async (
  formData: IEditOpportunity,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const { id, ...payload } = formData;
    if (!id.trim()) throw new Error("A valid opportunity ID is required");
    const response = await axiosInstance.patch<ICreateOpportunityResponse>(
      `${opportunitiesUrl}/${encodeURIComponent(id)}`,
      payload,
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to update opportunity",
    );
    dispatch(editOpportunity(response.data.data));
    toast.success(response.data.message || "Opportunity updated successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update opportunity"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};

export const update_opportunity_status = async (
  id: number,
  dispatch: AppDispatch,
  currentStatus: string | number | boolean,
): Promise<boolean> => {
  if (!id) {
    toast.error("A valid opportunity ID is required");
    return false;
  }

  dispatch(setUpdatingOpportunityId(id));
  try {
    const response = await axiosInstance.patch<{
      success: boolean;
      message: string;
      data?: { status?: string | number | boolean } | null;
    }>(`${opportunitiesUrl}/${encodeURIComponent(id)}/status`);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to update opportunity status",
    );
    const returnedStatus = response.data.data?.status;
    const wasActive =
      currentStatus === "1" || currentStatus === 1 || currentStatus === true;
    const status =
      returnedStatus === undefined
        ? wasActive
          ? "0"
          : "1"
        : returnedStatus === true || returnedStatus === 1
          ? "1"
          : returnedStatus === false || returnedStatus === 0
            ? "0"
            : String(returnedStatus);
    dispatch(updateOpportunityStatus({ id, status }));
    toast.success(response.data.message || "Opportunity status updated");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to update opportunity status"));
    return false;
  } finally {
    dispatch(setUpdatingOpportunityId(null));
  }
};

export const delete_opportunity = async (
  id: number,
  dispatch: AppDispatch,
): Promise<boolean> => {
  if (!id) {
    toast.error("A valid opportunity ID is required");
    return false;
  }

  dispatch(setOpportunityLoadingId(id));
  try {
    const response = await axiosInstance.delete<{
      success: boolean;
      message: string;
    }>(`${opportunitiesUrl}/${encodeURIComponent(id)}`);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to delete opportunity",
    );
    dispatch(deleteOpportunity(id));
    toast.success(response.data.message || "Opportunity deleted successfully");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to delete opportunity"));
    return false;
  } finally {
    dispatch(setOpportunityLoadingId(null));
  }
};

export const get_opportunities_type = async (
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const response =
      await axiosInstance.get<IOpportunityTypesResponse>(opportunityTypesUrl);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to load opportunity types",
    );
    dispatch(setOpportunityTypes(response.data.data));
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load opportunity types"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};

export const create_type = async (
  form: OpportunityTypeCreate,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const response = await axiosInstance.post<ICreateOpportunityTypeResponse>(
      opportunityTypesUrl,
      form,
    );
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to create opportunity type",
    );
    dispatch(createOpportunityType(response.data.data));
    toast.success(response.data.message || "Opportunity type created");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to create opportunity type"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};

export const delete_type = async (
  id: number,
  dispatch: AppDispatch,
): Promise<boolean> => {
  if (!id) {
    toast.error("A valid opportunity type ID is required");
    return false;
  }

  dispatch(setOpportunityTypeLoadingId(id));
  try {
    const response = await axiosInstance.delete<{
      success: boolean;
      message: string;
    }>(`${opportunityTypesUrl}/${id}`);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to delete opportunity type",
    );
    dispatch(deleteType(id));
    toast.success(response.data.message || "Opportunity type deleted");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to delete opportunity type"));
    return false;
  } finally {
    dispatch(setOpportunityTypeLoadingId(null));
  }
};

export const get_total_opportunity = async (
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const response = await axiosInstance.get<IOpportunityCount>(
      `${opportunitiesUrl}/analytics`,
    );
    if (response.data.success !== undefined) {
      assertSuccess(
        response.data.success,
        response.data.message || "",
        "Failed to load opportunity total",
      );
    }
    dispatch(setTotalOpportunity(response.data.data));
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to load opportunity total"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};

export const upload = async (
  formData: FormData,
  dispatch: AppDispatch,
): Promise<boolean> => {
  dispatch(setOpportunityLoading());
  try {
    const response = await axiosInstance.post<{
      success: boolean;
      message: string;
      data: Record<string, unknown>;
    }>("/upload", formData);
    assertSuccess(
      response.data.success,
      response.data.message,
      "Failed to upload opportunity file",
    );
    dispatch(setUpload(response.data.data));
    toast.success(response.data.message || "Upload successful");
    return true;
  } catch (error) {
    toast.error(getErrorMessage(error, "Failed to upload opportunity file"));
    return false;
  } finally {
    dispatch(disableOpportunityLoading());
  }
};
